import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Collapse, Alert, Button, Box, Typography, IconButton } from "@mui/material";
import ReactJsonViewCompare from "react-json-view-compare";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import DynamicFormField from "../../components/Appearance/DynamicFormField";
import Dialog from "../../components/Dialog.js";
// import useAutoSave from "../../hooks/useAutoSave";
import useFetch from "../../hooks/useFetch";
import usePutRequest from "../../hooks/usePutRequest";
import usePostRequest from "../../hooks/usePostRequest.js";
import "./Appearance.css";
import {
  ExpandLessIcon,
  ExpandMoreIcon,
} from "../../components/utils/InsyncIconsUtil";
import LoadingSpinner from "../../components/LoadingSpinner";
import TriggerPoints from "../../components/TriggerPoints";
import { usePageMeta } from "../../contexts/PageMetaContext";
import { SLATE, INDIGO } from "../../constants/Colors";
import {
  APPEARANCE_GET_ENDPOINT,
  APPEARANCE_UPDATE_ENDPOINT,
  APPEARANCE_PROD_DIFF_ENDPOINT,
  APPEARANCE_IMAGE_UPLOAD_ENDPOINT,
} from "../../constants/Constants";
import BottomActionBar from "../../components/BottomActionBar";
import ChatAppearance from "./ChatAppearance";

// Utility function to map internal names to form state names
const mapApiToFormState = (apiData, fieldsConfig) => {
  const formState = {};

  Object.keys(fieldsConfig).forEach((section) => {
    fieldsConfig[section].forEach((field) => {
      if (field.internalName) {
        formState[field.name] =
          apiData[field.internalName] ?? field.defaultValue;
      }
    });
  });

  return formState;
};

// Utility function to map form state fields to API payload fields
const mapFormStateToApi = (formState, fieldsConfig) => {
  const apiPayload = {};

  Object.keys(fieldsConfig).forEach((section) => {
    fieldsConfig[section].forEach((field) => {
      if (field.internalName && (formState[field.name] || typeof formState[field.name] === 'boolean')) {
        apiPayload[field.internalName] = formState[field.name];
      }
    });
  });
  return apiPayload;
};

const mergeConfigArrays = (baseConfig, additionalConfig) => {
  return Object.entries(additionalConfig).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: [...(acc[key] || []), ...value],
    };
  }, baseConfig);
};

const mergeFieldsConfig = (basicFields, advancedFields, imageAndIconFields) => {
  return [imageAndIconFields, advancedFields].reduce(
    (config, fields) => mergeConfigArrays(config, fields),
    { ...basicFields }
  );
};

const CARD_SX = {
  bgcolor: "#ffffff",
  border: `1px solid ${SLATE[200]}`,
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
  mb: 2,
};

const CARD_HEADER_SX = {
  px: 3,
  py: "11px",
  borderBottom: `1px solid ${SLATE[200]}`,
  bgcolor: SLATE[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const FIELD_ROW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  py: "10px",
  borderBottom: `1px solid ${SLATE[100]}`,
  "&:last-child": { borderBottom: "none" },
};

const FIELD_LABEL_SX = {
  fontSize: "0.875rem",
  color: SLATE[700],
  flexShrink: 0,
  maxWidth: "50%",
};

const Appearance = () => {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const selectedAgentSiteCode = useSelector(
    (state) => state?.apps?.selectedAgentSiteCode
  );
  const { setPageMeta } = usePageMeta();
  // const [pendingSave, setPendingSave] = useState(false);

  useEffect(() => {
    setPageMeta({ description: "Customize the look and feel of your chat widget." });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [formState, setFormState] = useState({});
  const [basicOpen] = useState(true);
  const [imagesIconOpen, setImagesIconOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [basicSettingsFields, setBasicSettingsFields] = useState({});
  const [advancedSettingsFields, setAdvancedSettingsFields] = useState({});
  const [imageAndIconFields, setImageAndIconsFields] = useState({});
  const [mergedFieldsConfig, setMergedFieldsConfig] = useState({});

  const isSaveDisabled = useRef(true);
  const [disabledSave, setDisabledSave] = useState(true);

  const [open, setOpen] = useState(false);

  const [oldStyleJson, setOldStyleJson] = useState({});
  const [newStyleJson, setNewStyleJson] = useState({});

  const [triggerPointsDetails, setTriggerPointsDetails] = useState({
    type: "select",
    label: "Trigger Points",
    name: "triggerPoints",
    options: currentApp?.trigger_points || [],
    selectedValue: selectedAgentSiteCode || "",
  });

  // Fetch initial data
  const {
    data: responseData,
    loading,
    error,
    fetchData,
  } = useFetch(
    `${APPEARANCE_GET_ENDPOINT}?appId=${currentApp?.id}&triggerPoint=${triggerPointsDetails.selectedValue}`,
    tokens,
    []
  );

  const { sendPutRequest } = usePutRequest(
    `${APPEARANCE_UPDATE_ENDPOINT}?appId=${currentApp?.id}&triggerPoint=${triggerPointsDetails.selectedValue}`,
    tokens
  );

  const { sendPostRequest } = usePostRequest(
    `${APPEARANCE_PROD_DIFF_ENDPOINT}?appId=${currentApp?.id}&triggerPoint=${triggerPointsDetails.selectedValue}`,
    tokens
  );

  // Map fetched data to formState
  useEffect(() => {
    if (!isEmpty(responseData)) {
      const data = responseData?.data;
      setBasicSettingsFields(responseData?.fields_data?.BASIC_SETTINGS_FIELDS);
      setAdvancedSettingsFields(
        responseData?.fields_data?.ADVANCED_SETTINGS_FIELDS
      );
      setImageAndIconsFields(responseData?.fields_data?.IMAGES_AND_ICONS);
      const mergedFieldsConfigLocal = mergeFieldsConfig(
        responseData?.fields_data?.BASIC_SETTINGS_FIELDS,
        responseData?.fields_data?.IMAGES_AND_ICONS,
        responseData?.fields_data?.ADVANCED_SETTINGS_FIELDS
      );
      setMergedFieldsConfig(mergedFieldsConfigLocal);

      const basicFormState = mapApiToFormState(data, mergedFieldsConfigLocal);
      const imagesAndIconsFormState = mapApiToFormState(
        data,
        responseData?.fields_data?.IMAGES_AND_ICONS
      );
      const advancedFormState = mapApiToFormState(
        data,
        responseData?.fields_data?.ADVANCED_SETTINGS_FIELDS
      );
      setFormState({
        ...basicFormState,
        ...imagesAndIconsFormState,
        ...advancedFormState,
      });
    }
    // else {
    //   // If no initial data, apply default values
    //   const basicDefaultState = mapApiToFormState({}, mergedFieldsConfig);
    //   const advancedDefaultState = mapApiToFormState(
    //     {},
    //     advancedSettingsFields
    //   );
    //   setFormState({ ...basicDefaultState, ...advancedDefaultState });
    // }
  }, [responseData]);

  // For saving configurations
  // useEffect(() => {
  //   if (pendingSave) {
  //     saveConfigurations();
  //     setPendingSave(false);
  //   }
  // }, [formState, pendingSave]);

  const fetchJsons = () => {
    const payload = mapFormStateToApi(formState, mergedFieldsConfig);
    sendPostRequest({ appearance: { value: payload } })
      .then((data) => {
        // console.log(data);
        setOldStyleJson(data.current_prod_style_guide);
        setNewStyleJson(data.current_stage_style_guide);
        setOpen(true);
      })
      .catch((error) => {
        console.log("error: ", error);
        toast.error(error.message);
      });
  };

  const handleSelectionChange = (value) => {
    setTriggerPointsDetails((prevState) => ({
      ...prevState,
      selectedValue: value,
    }));
    fetchData(
      `${APPEARANCE_GET_ENDPOINT}?appId=${currentApp?.id}&triggerPoint=${value}`
    );
  };

  const handleInputChange = (e) => {
    isSaveDisabled.current = false;
    let { name, value, type, checked } = e.target;
    if (type === "image") name = "defaultValue";
    // Allow setting field to empty string.

    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleImmediateChange = (e) => {
    handleInputChange(e);
    //setPendingSave(true);
  };

  // const handleFieldBlur = () => {
  //   setPendingSave(true);
  // };

  // Define saveConfigurations function
  const saveConfigurations = (targetEnv) => {
    const payload = mapFormStateToApi(formState, mergedFieldsConfig);

    sendPutRequest({ appearance: { value: payload }, targetEnv: targetEnv })
      .then(() => {
        isSaveDisabled.current = true;
        console.log("Successfully updated form state");
        toast.success(`Success!`);
      })
      .catch((error) => {
        console.log("error: ", error);
        toast.error(error.message);
      });
  };

  const pushToProductionBtn = {
    name: "Push to production",
    onClick: fetchJsons,
  };

  // Use effect to enable save buttton when state changes
  useEffect(() => {
    if (isSaveDisabled.current) {
      setDisabledSave(true);
    } else {
      setDisabledSave(false);
    }
  }, [formState]);

  // Use the handleBlur hook
  // const { handleBlur } = useAutoSave(saveConfigurations, [formState]);

  if (loading) {
    return <LoadingSpinner message="Loading Appearance..." />;
  }

  if (error) {
    toast.error(error);
    return <Alert severity="error">Failed to load appearance settings.</Alert>;
  }

  const renderFieldRow = (field, section, onChange, extraProps = {}) => (
    <Box key={`${section}-${field.name}-wrapper`} sx={FIELD_ROW_SX}>
      {(field.type === "text" || field.type === "number" || field.type === "image") ? (
        <DynamicFormField field={field} value={formState[field.name]} onChange={onChange} {...extraProps} />
      ) : (
        <>
          <Typography sx={FIELD_LABEL_SX}>{field.label}</Typography>
          <DynamicFormField field={field} value={formState[field.name]} onChange={onChange} {...extraProps} />
        </>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", width: "100%" }}>
      <Box sx={{ pb: 10, flex: "1 1 0", minWidth: 0 }}>
          {!selectedAgentSiteCode && currentApp?.trigger_points?.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <TriggerPoints
                app={currentApp}
                onChange={handleSelectionChange}
                triggerPoint={triggerPointsDetails.selectedValue}
              />
            </Box>
          )}

          {/* Basic settings — one card per subsection */}
          <Collapse in={basicOpen} timeout="auto" unmountOnExit>
            {basicSettingsFields &&
              Object.keys(basicSettingsFields).map((section) => (
                <Box key={`${section}-wrapper`} sx={CARD_SX}>
                  <Box sx={CARD_HEADER_SX}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
                      {section}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 3 }}>
                    {basicSettingsFields[section]
                      .filter((i) => !i.isHidden)
                      .map((field) => renderFieldRow(field, section, handleInputChange, { imageLabelStyleVariant: "" }))}
                  </Box>
                </Box>
              ))}
          </Collapse>

          {/* Images & Icons — collapsible card */}
          <Box sx={CARD_SX}>
            <Box sx={CARD_HEADER_SX}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
                Images & Icons
              </Typography>
              <IconButton size="small" onClick={() => setImagesIconOpen((prev) => !prev)} sx={{ color: SLATE[500] }}>
                {imagesIconOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={imagesIconOpen} timeout="auto" unmountOnExit>
              <Box sx={{ px: 3 }}>
                {imageAndIconFields &&
                  Object.keys(imageAndIconFields).map((section) =>
                    imageAndIconFields[section]
                      .filter((i) => !i.isHidden)
                      .map((field) =>
                        renderFieldRow(field, section, handleInputChange, {
                          onImmediateChange: handleImmediateChange,
                          endpoint: field.type === "image"
                            ? `${APPEARANCE_IMAGE_UPLOAD_ENDPOINT}?appId=${currentApp?.id}&triggerPoint=${triggerPointsDetails.selectedValue}`
                            : "",
                        })
                      )
                  )}
              </Box>
            </Collapse>
          </Box>

          {/* Advanced Settings — collapsible card, one per subsection */}
          <Box sx={{ ...CARD_SX, mb: 0 }}>
            <Box sx={CARD_HEADER_SX}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
                Advanced Settings
              </Typography>
              <IconButton size="small" onClick={() => setAdvancedOpen((prev) => !prev)} sx={{ color: SLATE[500] }}>
                {advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
              {advancedSettingsFields &&
                Object.keys(advancedSettingsFields).map((section, idx, arr) => (
                  <Box key={`${section}-wrapper`}>
                    {Object.keys(advancedSettingsFields).length > 1 && (
                      <Box sx={{
                        px: 3,
                        py: "8px",
                        bgcolor: SLATE[50],
                        borderTop: idx > 0 ? `1px solid ${SLATE[200]}` : "none",
                        borderBottom: `1px solid ${SLATE[200]}`,
                      }}>
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: SLATE[600] }}>
                          {section}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ px: 3 }}>
                      {advancedSettingsFields[section]
                        .filter((i) => !i.isHidden)
                        .map((field) => renderFieldRow(field, section, handleInputChange))}
                    </Box>
                  </Box>
                ))}
            </Collapse>
          </Box>

          <Dialog
            onClose={() => setOpen(false)}
            open={open}
            title="Are you sure?"
            content={
              <ReactJsonViewCompare
                oldData={oldStyleJson}
                newData={newStyleJson}
              />
            }
            actions={
              <div>
                <Button
                  sx={{ marginRight: "15px" }}
                  variant="SecondaryButton"
                  onClick={() => setOpen(false)}
                >
                  No
                </Button>
                <Button
                  variant="SecondaryButton"
                  onClick={() => {
                    saveConfigurations("production");
                    setOpen(false);
                  }}
                >
                  Yes
                </Button>
              </div>
            }
          />
      </Box>
      <Box sx={{ flex: "0 0 400px", position: "sticky", top: 16, alignSelf: "flex-start" }}>
        <ChatAppearance
          market={currentApp?.market}
          mapFormStateToApi={mapFormStateToApi}
          formState={formState}
          mergedFieldsConfig={mergedFieldsConfig}
          triggerPoint={triggerPointsDetails.selectedValue}
        />
      </Box>
      </Box>
      <BottomActionBar
        name="Save to Dev & Stage"
        disabledSave={disabledSave}
        handleSave={() => saveConfigurations()}
        buttons={[pushToProductionBtn]}
      />
    </>
  );
};

export default Appearance;
